import { useContext, useEffect, useMemo, useState } from "react";

// react-router components
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import { ThemeProvider } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Configurator from "components/Configurator";
import Sidenav from "components/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import rtlPlugin from "stylis-plugin-rtl";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { setMiniSidenav, setOpenConfigurator, useMaterialUIController } from "context";

// Images
import { default as brandDark, default as brandWhite } from "assets/images/logo.png";

import ForgotPassword from "auth/forgot-password";
import Login from "auth/login";
import Register from "auth/register";
import ResetPassword from "auth/reset-password";
import MDLoader from "components/MDLoader";
import ProtectedRoute from "components/ProtectedRoute";
import { AuthContext, PreserveTableProvider, useCache } from "context";
import Demo from "demo";
import AgentForm from "layouts/manage-agents/form";
import CallForm from "layouts/manage-calls/form";
import ClientForm from "layouts/manage-clients/form";
import Parameter from "layouts/manage-parameters/form";
import StatusForm from "layouts/manage-statuses/form";
import PermissionForm from "layouts/permission/form";
import UserManagement from "layouts/user-management";
import UserManagementForm from "layouts/user-management/form";
import UserProfile from "layouts/user-profile";
import { Helmet } from "react-helmet";
import { setupAxiosInterceptors } from "./services/interceptor";

export default function App() {
  const authContext = useContext(AuthContext);
  const { pageLoader } = useCache();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // if the token expired or other errors it logs out and goes to the login page
  const navigate = useNavigate();
  setupAxiosInterceptors(() => {
    authContext.logout();
    navigate("/auth/login");
  });

  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    setIsDemo(process.env.REACT_APP_IS_DEMO === "true");
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route && route.type !== "auth") {
        return (
          <Route
            exact
            path={route.route}
            element={
              <ProtectedRoute
                isAuthenticated={authContext.isAuthenticated}
                refresh={authContext.refresh}
                isLoader={authContext.isLoader}
              >
                {route.component}
              </ProtectedRoute>
            }
            key={route.key}
          />
        );
      }
      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <>
      {isDemo && (
        <Helmet>
          <meta name="keywords" content="" />
          <meta name="description" content="" />
          <meta itemProp="name" content="Vocalyn - Where Voice Meets Intelligence" />
          <meta itemProp="description" content="" />
          <meta itemProp="image" content="" />
          <meta name="twitter:card" content="product" />
          <meta name="twitter:site" content="@techhighway" />
          <meta name="twitter:title" content="Vocalyn - Where Voice Meets Intelligence" />
          <meta name="twitter:description" content="" />
          <meta name="twitter:creator" content="@techhighway" />
          <meta name="twitter:image" content="" />
          <meta property="og:title" content="Vocalyn - Where Voice Meets Intelligence" />
          <meta property="og:type" content="article" />
          <meta property="og:url" content="" />
          <meta property="og:image" content="" />
          <meta property="og:description" content="" />
          <meta property="og:site_name" content="Creative Tim" />
        </Helmet>
      )}
      {direction === "rtl" ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
            <PreserveTableProvider>
              <CssBaseline />
              {layout === "dashboard" && (
                <>
                  <Sidenav
                    color={sidenavColor}
                    brand={
                      (transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite
                    }
                    brandName="Vocalyn"
                    routes={routes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                  <Configurator />
                  {/* {configsButton} */}
                </>
              )}
              {layout === "vr" && <Configurator />}
              <Routes>
                <Route path="login" element={<Navigate to="/auth/login" />} />
                <Route path="register" element={<Navigate to="/auth/register" />} />
                <Route path="forgot-password" element={<Navigate to="/auth/forgot-password" />} />
                <Route
                  path="/permission/:action"
                  element={
                    <ProtectedRoute
                      isAuthenticated={authContext.isAuthenticated}
                      refresh={authContext.refresh}
                      isLoader={authContext.isLoader}
                    >
                      <PermissionForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage-parameters/:action"
                  element={
                    <ProtectedRoute
                      isAuthenticated={authContext.isAuthenticated}
                      refresh={authContext.refresh}
                      isLoader={authContext.isLoader}
                    >
                      <Parameter />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage-statuses/:action"
                  element={
                    <ProtectedRoute
                      isAuthenticated={authContext.isAuthenticated}
                      refresh={authContext.refresh}
                      isLoader={authContext.isLoader}
                    >
                      <StatusForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage-agents/:action"
                  element={
                    <ProtectedRoute
                      isAuthenticated={authContext.isAuthenticated}
                      refresh={authContext.refresh}
                      isLoader={authContext.isLoader}
                    >
                      <AgentForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage-clients/:action"
                  element={
                    <ProtectedRoute
                      isAuthenticated={authContext.isAuthenticated}
                      refresh={authContext.refresh}
                      isLoader={authContext.isLoader}
                    >
                      <ClientForm />
                    </ProtectedRoute>
                  }
                />
                {getRoutes(routes)}
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
            </PreserveTableProvider>
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <PreserveTableProvider>
            <CssBaseline />
            {layout === "dashboard" && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                  brandName="Vocalyn"
                  routes={routes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <Configurator />
                {/* {configsButton} */}
              </>
            )}
            {layout === "vr" && <Configurator />}
            <Routes>
              <Route path="/demo" element={<Demo />} />
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/register" element={<Register />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route
                path="/permission/:action"
                element={
                  <ProtectedRoute
                    isAuthenticated={authContext.isAuthenticated}
                    refresh={authContext.refresh}
                    isLoader={authContext.isLoader}
                  >
                    <PermissionForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage-parameters/:action"
                element={
                  <ProtectedRoute
                    isAuthenticated={authContext.isAuthenticated}
                    refresh={authContext.refresh}
                    isLoader={authContext.isLoader}
                  >
                    <Parameter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage-statuses/:action"
                element={
                  <ProtectedRoute
                    isAuthenticated={authContext.isAuthenticated}
                    refresh={authContext.refresh}
                    isLoader={authContext.isLoader}
                  >
                    <StatusForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage-agents/:action"
                element={
                  <ProtectedRoute
                    isAuthenticated={authContext.isAuthenticated}
                    refresh={authContext.refresh}
                    isLoader={authContext.isLoader}
                  >
                    <AgentForm />
                  </ProtectedRoute>
                }
              />
              <Route
                  path="/manage-clients/:action"
                  element={
                    <ProtectedRoute
                      isAuthenticated={authContext.isAuthenticated}
                      refresh={authContext.refresh}
                      isLoader={authContext.isLoader}
                    >
                      <ClientForm />
                    </ProtectedRoute>
                  }
                />
              <Route
                exact
                path="user-profile"
                element={
                  <ProtectedRoute
                    isAuthenticated={authContext.isAuthenticated}
                    refresh={authContext.refresh}
                    isLoader={authContext.isLoader}
                  >
                    <UserProfile />
                  </ProtectedRoute>
                }
                key="user-profile"
              />
              <Route
                exact
                path="user-management"
                element={
                  <ProtectedRoute
                    isAuthenticated={authContext.isAuthenticated}
                    refresh={authContext.refresh}
                    isLoader={authContext.isLoader}
                  >
                    <UserManagement />
                  </ProtectedRoute>
                }
                key="user-management"
              />
              <Route
                exact
                path="user-management/:action"
                element={
                  <ProtectedRoute
                    isAuthenticated={authContext.isAuthenticated}
                    refresh={authContext.refresh}
                    isLoader={authContext.isLoader}
                  >
                    <UserManagementForm />
                  </ProtectedRoute>
                }
                key="user-management"
              />
              <Route
                path="/manage-calls/:action"
                element={
                  <ProtectedRoute
                    isAuthenticated={authContext.isAuthenticated}
                    refresh={authContext.refresh}
                    isLoader={authContext.isLoader}
                  >
                    <CallForm />
                  </ProtectedRoute>
                }
              />
              {getRoutes(routes)}
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </PreserveTableProvider>
          {pageLoader ? <MDLoader /> : ""}
        </ThemeProvider>
      )}
    </>
  );
}
